import * as R from 'ramda';
import * as A from './actions';
import * as G from '../../helpers';
import * as GC from '../../constants';
import { canReuseReport } from './helpers';
import { visitPageSaga } from '../../sagas';
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
import { openLoader, closeLoader } from '../../components/loader/actions';
import { PAGE_LANE_RPM, PAGE_WEEKLY_RPM, PAGE_FLEET_ACTIVITY } from './constants';
import { put, call, fork, take, cancel, select, cancelled, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  makeSelectReport,
  makeSelectDetail,
  makeSelectOptionList,
  makeSelectSyncRequest,
} from './selectors';
//////////////////////////////////////////////////

let requestSequence = 0;

const driverListFields = G.mapIndexed(
  (name, i) => ({ name, sequence: R.inc(i) }),
  [GC.FIELD_LAST_NAME, GC.FIELD_FIRST_NAME, GC.FIELD_BRANCH_GUID, GC.FIELD_FLEET_LOGIN_ID, GC.FIELD_FLEET_VENDOR_GUID],
);

function* getAnalyticsWeeklyRpmByRegionReportRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectReport(PAGE_WEEKLY_RPM, key);
  const current = yield select(selector);

  if (append && (!R.prop('applied', current) || current.loading)) return;
  if (!append && payload.filters) {
    yield put(A.setFilters({ ...payload.filters, page: PAGE_WEEKLY_RPM }));
  }

  if (!append && canReuseReport(current, payload)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    requestId: requestSequence,
  };

  const initialLoad = !R.prop('report', current);

  try {
    if (initialLoad) yield put(openLoader());

    yield put(A.getAnalyticsWeeklyRpmByRegionReportLoading(request));
    yield put(A.setWeeklyRpmLoading({ ...request, loading: true }));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsWeeklyRpmByRegionReport, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsWeeklyRpmByRegionReportSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsWeeklyRpmByRegionReportFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsWeeklyRpmByRegionReportRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsWeeklyRpmByRegionReportFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsWeeklyRpmByRegionReportRequest exception');
  } finally {
    if (yield cancelled()) {
      yield put(A.getAnalyticsWeeklyRpmByRegionReportFailure({ ...request, error: { cancelled: true } }));
    }

    yield put(A.setWeeklyRpmLoading({ ...request, loading: false }));

    if (initialLoad) yield put(closeLoader());
  }
}

function* getAnalyticsWeeklyRpmByRegionReportOrdersRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectDetail(PAGE_WEEKLY_RPM, key, payload.rowKey);
  const current = yield select(selector);

  const report = yield select(makeSelectReport(PAGE_WEEKLY_RPM, key));

  if (!R.prop('applied', report) || report.loading || R.prop('loading', current)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    rowKey: payload.rowKey,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsWeeklyRpmByRegionReportOrdersLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsWeeklyRpmByRegionReportOrders, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsWeeklyRpmByRegionReportOrdersSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsWeeklyRpmByRegionReportOrdersFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsWeeklyRpmByRegionReportOrdersRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsWeeklyRpmByRegionReportOrdersFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsWeeklyRpmByRegionReportOrdersRequest exception');
  }
}

function* getAnalyticsFleetActivityReportRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectReport(PAGE_FLEET_ACTIVITY, key);
  const current = yield select(selector);

  if (append && (!R.prop('applied', current) || current.loading)) return;
  if (!append && payload.filters) {
    yield put(A.setFilters({ ...payload.filters, page: PAGE_FLEET_ACTIVITY }));
  }

  if (!append && canReuseReport(current, payload)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    requestId: requestSequence,
  };

  const initialLoad = !R.prop('report', current);

  try {
    if (initialLoad) yield put(openLoader());

    yield put(A.getAnalyticsFleetActivityReportLoading(request));
    yield put(A.setFleetActivityLoading({ ...request, loading: true }));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsFleetActivityReport, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsFleetActivityReportSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsFleetActivityReportFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsFleetActivityReportRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsFleetActivityReportFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsFleetActivityReportRequest exception');
  } finally {
    if (yield cancelled()) {
      yield put(A.getAnalyticsFleetActivityReportFailure({ ...request, error: { cancelled: true } }));
    }

    yield put(A.setFleetActivityLoading({ ...request, loading: false }));

    if (initialLoad) yield put(closeLoader());
  }
}

function* getAnalyticsFleetActivityReportTelsRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectDetail(PAGE_FLEET_ACTIVITY, key, payload.rowKey);
  const current = yield select(selector);

  const report = yield select(makeSelectReport(PAGE_FLEET_ACTIVITY, key));

  if (!R.prop('applied', report) || report.loading || R.prop('loading', current)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    rowKey: payload.rowKey,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsFleetActivityReportTelsLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsFleetActivityReportTels, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsFleetActivityReportTelsSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsFleetActivityReportTelsFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsFleetActivityReportTelsRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsFleetActivityReportTelsFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsFleetActivityReportTelsRequest exception');
  }
}

function* getAnalyticsLaneRpmReportRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectReport(PAGE_LANE_RPM, key);
  const current = yield select(selector);

  if (append && (!R.prop('applied', current) || current.loading)) return;
  if (!append && payload.filters) {
    yield put(A.setFilters({ ...payload.filters, page: PAGE_LANE_RPM }));
  }

  if (!append && canReuseReport(current, payload)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    requestId: requestSequence,
  };

  const initialLoad = !R.prop('report', current);

  try {
    if (initialLoad) yield put(openLoader());

    yield put(A.getAnalyticsLaneRpmReportLoading(request));
    yield put(A.setLaneRpmLoading({ ...request, loading: true }));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsLaneRpmReport, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsLaneRpmReportSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsLaneRpmReportFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsLaneRpmReportRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsLaneRpmReportFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsLaneRpmReportRequest exception');
  } finally {
    if (yield cancelled()) {
      yield put(A.getAnalyticsLaneRpmReportFailure({ ...request, error: { cancelled: true } }));
    }

    yield put(A.setLaneRpmLoading({ ...request, loading: false }));

    if (initialLoad) yield put(closeLoader());
  }
}

function* getAnalyticsLaneRpmReportLanesRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectReport(PAGE_LANE_RPM, key);
  const current = yield select(selector);

  if (append && (!R.prop('applied', current) || current.loading)) return;
  if (!append && payload.filters) {
    yield put(A.setFilters({ ...payload.filters, page: PAGE_LANE_RPM }));
  }

  if (!append && canReuseReport(current, payload)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    requestId: requestSequence,
  };

  const initialLoad = !R.prop('report', current);

  try {
    if (initialLoad) yield put(openLoader());

    yield put(A.getAnalyticsLaneRpmReportLanesLoading(request));
    yield put(A.setLaneRpmLoading({ ...request, loading: true }));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsLaneRpmReportLanes, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsLaneRpmReportLanesSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsLaneRpmReportLanesFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsLaneRpmReportLanesRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsLaneRpmReportLanesFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsLaneRpmReportLanesRequest exception');
  } finally {
    if (yield cancelled()) {
      yield put(A.getAnalyticsLaneRpmReportLanesFailure({ ...request, error: { cancelled: true } }));
    }

    yield put(A.setLaneRpmLoading({ ...request, loading: false }));

    if (initialLoad) yield put(closeLoader());
  }
}

function* getAnalyticsLaneRpmReportLaneOrdersRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectDetail(PAGE_LANE_RPM, key, payload.rowKey);
  const current = yield select(selector);

  const report = yield select(makeSelectReport(PAGE_LANE_RPM, key));

  if (!R.prop('applied', report) || report.loading || R.prop('loading', current)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    rowKey: payload.rowKey,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsLaneRpmReportLaneOrdersLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsLaneRpmReportLaneOrders, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsLaneRpmReportLaneOrdersSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsLaneRpmReportLaneOrdersFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsLaneRpmReportLaneOrdersRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsLaneRpmReportLaneOrdersFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsLaneRpmReportLaneOrdersRequest exception');
  }
}

function* getAnalyticsLaneRpmReportLaneCustomersRequest(action) {
  const { payload } = action;
  const key = payload.key || 'report';
  const append = payload.data.offset > 0;
  const selector = makeSelectDetail(PAGE_LANE_RPM, key, payload.rowKey);
  const current = yield select(selector);

  const report = yield select(makeSelectReport(PAGE_LANE_RPM, key));

  if (!R.prop('applied', report) || report.loading || R.prop('loading', current)) return;

  requestSequence += 1;

  const request = {
    key,
    append,
    request: action,
    rowKey: payload.rowKey,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsLaneRpmReportLaneCustomersLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsLaneRpmReportLaneCustomers, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsLaneRpmReportLaneCustomersSuccess({ ...request, data: res.data }));
    } else {
      yield put(A.getAnalyticsLaneRpmReportLaneCustomersFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsLaneRpmReportLaneCustomersRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsLaneRpmReportLaneCustomersFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsLaneRpmReportLaneCustomersRequest exception');
  }
}

function* getAnalyticsRegionsRequest(action) {
  const { payload } = action;
  const { page } = payload;
  const branch = payload.data.currentEnterprise;
  const selector = makeSelectOptionList(page, 'regions');
  const current = yield select(selector);

  if (G.isNotNil(branch) && R.prop('loadedFor', current) === branch) return;
  if (R.prop('loading', current) && current.request.payload.data.currentEnterprise === branch) return;

  requestSequence += 1;

  const request = {
    page,
    branch,
    request: action,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsRegionsLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsRegions, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      const items = R.map(item => ({ value: item.guid, label: item.name }), res.data || []);

      yield put(A.getAnalyticsRegionsSuccess({ ...request, data: items }));
    } else {
      yield put(A.getAnalyticsRegionsFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getAnalyticsRegionsRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsRegionsFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getAnalyticsRegionsRequest exception');
  }
}

function* getListFleetDriversDownRequest(action) {
  const { payload } = action;
  const { page } = payload;
  const branch = payload.data.currentEnterprise;
  const selector = makeSelectOptionList(page, 'drivers');
  const current = yield select(selector);

  if (G.isNotNil(branch) && R.prop('loadedFor', current) === branch) return;
  if (R.prop('loading', current) && current.request.payload.data.currentEnterprise === branch) return;

  requestSequence += 1;

  const request = {
    page,
    branch,
    request: action,
    requestId: requestSequence,
  };

  try {
    yield put(A.getListFleetDriversDownLoading(request));

    const data = {
      orderFields: [
        { sequence: 1, reference: false, order: GC.SORT_TYPE_ASC, name: GC.FIELD_FIRST_NAME },
        { sequence: 2, reference: false, order: GC.SORT_TYPE_ASC, name: GC.FIELD_LAST_NAME },
      ],
      pageable: false,
      fields: driverListFields,
      [GC.FIELD_CURRENT_BRANCH]: branch,
    };
    const res = yield call(sendRequest, 'post', endpointsMap.listFleetDriversDown, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      const items = R.map(
        item => ({ value: G.getGuidFromObject(item), label: G.getUserInfo(item).fullText }),
        res.data || [],
      );

      yield put(A.getListFleetDriversDownSuccess({ ...request, data: items }));
    } else {
      yield put(A.getListFleetDriversDownFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));

      yield call(G.handleFailResponseSimple, res, true, 'getListFleetDriversDownRequest fail');
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getListFleetDriversDownFailure({ ...request, error: { status: null } }));

    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'getListFleetDriversDownRequest exception');
  }
}

function* getAnalyticsSyncStatusRequest(action) {
  const { payload } = action;
  const branch = payload.data.currentEnterprise;
  const selector = makeSelectSyncRequest(branch);
  const current = yield select(selector);

  if (R.prop('loading', current) && current.request.payload.data.currentEnterprise === branch) return;

  if (R.prop('phase', current) === 'success' && Date.now() - current.receivedAt < 30000) return;

  requestSequence += 1;

  const request = {
    branch,
    request: action,
    requestId: requestSequence,
  };

  try {
    yield put(A.getAnalyticsSyncStatusLoading(request));

    const { data } = payload;
    const res = yield call(sendRequest, 'post', endpointsMap.analyticsSyncStatus, { data });

    if (!res) throw new Error('Analytics request returned no response');

    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    if (G.isResponseSuccess(res.status)) {
      yield put(A.getAnalyticsSyncStatusSuccess({ ...request, data: res.data, receivedAt: Date.now() }));
    } else {
      yield put(A.getAnalyticsSyncStatusFailure({
        ...request,
        error: { status: R.prop('status', res) || null },
      }));
    }
  } catch (error) {
    const latest = yield select(selector);

    if (R.prop('requestId', latest) !== request.requestId) return;

    yield put(A.getAnalyticsSyncStatusFailure({ ...request, error: { status: null } }));
    yield call(G.handleException, error, 'getAnalyticsSyncStatusRequest exception');
  }
}

function* exportAnalyticsReportRequest({ payload }) {
  try {
    yield put(openLoader());

    const options = {
      resType: 'arraybuffer',
      data: R.omit(['limit', 'offset'], payload.data),
    };

    const res = yield call(sendRequest, 'post', payload.endpoint, options);

    if (G.isResponseSuccess(res.status)) {
      yield call(G.saveFileFromResponse, res);
    } else {
      yield call(
        G.handleFailResponseSimple,
        G.convertArrayBufferFailResponse(res),
        true,
        'exportAnalyticsReportRequest fail',
      );
    }
  } catch (error) {
    yield call(G.showToastrMessageSimpleCatchDefault);
    yield call(G.handleException, error, 'exportAnalyticsReportRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* visitFleetActivityReportPageSaga({ payload }) {
  yield call(visitPageSaga, payload, GC.CHECK_VISIT_FLEET_ACTIVITY_REPORT_PAGE);
}

function* visitWeeklyRpmByRegionReportPageSaga({ payload }) {
  yield call(visitPageSaga, payload, GC.CHECK_VISIT_WEEKLY_RPM_BY_REGION_REPORT_PAGE);
}

function* visitLaneRpmReportPageSaga({ payload }) {
  yield call(visitPageSaga, payload, GC.CHECK_VISIT_LANE_RPM_REPORT_PAGE);
}

function* analyticsRequestWatcherSaga() {
  // Different rows and report grains load independently; request IDs discard stale responses.
  yield takeEvery(A.getAnalyticsRegionsRequest, getAnalyticsRegionsRequest);
  yield takeEvery(A.exportAnalyticsReportRequest, exportAnalyticsReportRequest);
  yield takeEvery(A.getAnalyticsSyncStatusRequest, getAnalyticsSyncStatusRequest);
  yield takeEvery(A.getListFleetDriversDownRequest, getListFleetDriversDownRequest);
  yield takeEvery(A.getAnalyticsLaneRpmReportRequest, getAnalyticsLaneRpmReportRequest);
  yield takeEvery(A.getAnalyticsLaneRpmReportLanesRequest, getAnalyticsLaneRpmReportLanesRequest);
  yield takeEvery(A.getAnalyticsFleetActivityReportRequest, getAnalyticsFleetActivityReportRequest);
  yield takeEvery(A.getAnalyticsLaneRpmReportLaneOrdersRequest, getAnalyticsLaneRpmReportLaneOrdersRequest);
  yield takeEvery(A.getAnalyticsFleetActivityReportTelsRequest, getAnalyticsFleetActivityReportTelsRequest);
  yield takeEvery(A.getAnalyticsWeeklyRpmByRegionReportRequest, getAnalyticsWeeklyRpmByRegionReportRequest);
  yield takeEvery(A.getAnalyticsLaneRpmReportLaneCustomersRequest, getAnalyticsLaneRpmReportLaneCustomersRequest);
  yield takeEvery(A.getAnalyticsWeeklyRpmByRegionReportOrdersRequest, getAnalyticsWeeklyRpmByRegionReportOrdersRequest);
}

export function* analyticsWatcherSaga() {
  yield takeLatest(GC.VISIT_LANE_RPM_REPORT_PAGE, visitLaneRpmReportPageSaga);
  yield takeLatest(GC.VISIT_FLEET_ACTIVITY_REPORT_PAGE, visitFleetActivityReportPageSaga);
  yield takeLatest(GC.VISIT_WEEKLY_RPM_BY_REGION_REPORT_PAGE, visitWeeklyRpmByRegionReportPageSaga);


  let requestsTask = yield fork(analyticsRequestWatcherSaga);
  let insideAnalytics = false;

  while (true) { // eslint-disable-line no-constant-condition
    const { payload } = yield take(GC.LOCATION_CHANGE_ACTION);

    const pathname = R.propOr('', 'pathname', payload);

    const nextInsideAnalytics = pathname === GC.ROUTE_PATH_ANALYTICS
      || pathname.startsWith(`${GC.ROUTE_PATH_ANALYTICS}/`);

    if (insideAnalytics && !nextInsideAnalytics) {
      yield cancel(requestsTask);
      yield put(A.setInitialState());

      requestsTask = yield fork(analyticsRequestWatcherSaga);
    }

    insideAnalytics = nextInsideAnalytics;
  }
}

export default analyticsWatcherSaga;

export {
  getAnalyticsRegionsRequest,
  exportAnalyticsReportRequest,
  getAnalyticsSyncStatusRequest,
  getListFleetDriversDownRequest,
  getAnalyticsLaneRpmReportRequest,
  getAnalyticsLaneRpmReportLanesRequest,
  getAnalyticsFleetActivityReportRequest,
  getAnalyticsWeeklyRpmByRegionReportRequest,
  getAnalyticsFleetActivityReportTelsRequest,
  getAnalyticsLaneRpmReportLaneOrdersRequest,
  getAnalyticsLaneRpmReportLaneCustomersRequest,
  getAnalyticsWeeklyRpmByRegionReportOrdersRequest,
};
